import React, { useEffect, useState } from 'react'
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { IsEmptyGuid } from '../../helper/Formatters';
import { Modal, Form, Row, Col, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

const BANK_COLORS = {
    'กสิกรไทย': '#00a651', 'กรุงเทพ': '#1b4e9b', 'ไทยพาณิชย์': '#4e2b84',
    'กรุงไทย': '#009fda', 'กรุงศรี': '#f4a01c', 'ทหารไทยธนชาต': '#fc4c02',
    'ออมสิน': '#e8007d', 'ธอส': '#f58220',
};
const BANK_LIST = [
    'กสิกรไทย', 'กรุงเทพ', 'ไทยพาณิชย์', 'กรุงไทย',
    'กรุงศรี', 'ทหารไทยธนชาต', 'ออมสิน', 'ธอส',
];
const getBankColor = (name = '') => {
    for (const k of Object.keys(BANK_COLORS)) {
        if (name.includes(k)) return BANK_COLORS[k];
    }
    return '#6b7280';
};
const getBankInitial = (name = '') => (name || 'B')[0].toUpperCase();

const AccountsBox = ({ euid, readOnly = false, title = 'รายการบัญชี' }) => {
    const api = useApiServices();
    const [accounts, setAccounts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showBankList, setShowBankList] = useState(false);
    const [form, setForm] = useState({ bankName: '', accountNumber: '', accountName: '', branch: '', isActive: true });

    const fetchAccounts = async () => {
        const { isSuccess, result } = await api.GetBankAccountAsync(euid);
        if (isSuccess && result) setAccounts(result);
    };

    const handleToggleActive = async (acc) => {
        const { isSuccess } = await api.UpdateBankAccountAsync(euid, { ...acc, isActive: !acc.isActive });
        if (isSuccess) fetchAccounts();
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        const { isSuccess } = await api.AddBankAccountAsync(euid, form);
        setSubmitting(false);
        if (isSuccess) {
            toast.success('บันทึกข้อมูลแล้ว');
            fetchAccounts();
            setShowModal(false);
            setForm({ bankName: '', accountNumber: '', accountName: '', branch: '', isActive: true });
        } else {
            toast.error('บางอย่างผิดพลาด');
        }
    };

    const handleClose = () => {
        setShowModal(false);
        setForm({ bankName: '', accountNumber: '', accountName: '', branch: '', isActive: true });
    };

    useEffect(() => {
        if (euid && !IsEmptyGuid(euid)) fetchAccounts();
    }, [euid]);

    const sorted = [...accounts].sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return new Date(b.createDate) - new Date(a.createDate);
    });

    const bankColor = getBankColor(form.bankName);

    return (
        <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {title}
                    </span>
                    {accounts.length > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '1px 7px' }}>
                            {accounts.length}
                        </span>
                    )}
                </div>
                {!IsEmptyGuid(euid) && !readOnly && (
                    <button onClick={() => setShowModal(true)} style={addBtnStyle}>
                        <i className="fa fa-plus" style={{ fontSize: 10 }} />เพิ่มบัญชี
                    </button>
                )}
            </div>

            {/* List */}
            {sorted.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 13, background: '#f9fafb', borderRadius: 10 }}>
                    <i className="fa fa-university" style={{ fontSize: 20, marginBottom: 6, display: 'block', opacity: 0.4 }} />
                    ไม่พบบัญชี
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {sorted.map(acc => {
                        const color = getBankColor(acc.bankName || '');
                        return (
                            <div key={acc.id} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                padding: '12px 14px', borderRadius: 10,
                                background: acc.isActive ? '#fff' : '#f9fafb',
                                border: `1px solid ${acc.isActive ? '#e0e7ff' : '#e5e7eb'}`,
                                opacity: acc.isActive ? 1 : 0.6,
                                transition: 'all 0.15s',
                            }}>
                                <div style={{
                                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                    background: `${color}18`, border: `1.5px solid ${color}40`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 16, fontWeight: 700, color,
                                }}>
                                    {getBankInitial(acc.bankName)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937', marginBottom: 1 }}>{acc.accountName || '-'}</div>
                                    <div style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace', letterSpacing: '0.04em' }}>{acc.accountNumber || '-'}</div>
                                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                                        {acc.bankName || '-'}{acc.branch ? ` · ${acc.branch}` : ''}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                                    <span style={{
                                        fontSize: 10, fontWeight: 600, borderRadius: 10, padding: '2px 7px',
                                        background: acc.isActive ? '#dcfce7' : '#f3f4f6',
                                        color: acc.isActive ? '#16a34a' : '#9ca3af',
                                    }}>
                                        {acc.isActive ? 'ใช้งาน' : 'ปิด'}
                                    </span>
                                    {!readOnly && (
                                        <button onClick={() => handleToggleActive(acc)} style={{
                                            fontSize: 10, background: 'none', border: 'none', padding: 0,
                                            cursor: 'pointer', color: acc.isActive ? '#f87171' : '#22c55e', fontWeight: 500,
                                        }}>
                                            {acc.isActive ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add Modal */}
            <Modal show={showModal} onHide={handleClose} centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '18px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: `linear-gradient(135deg, ${bankColor}, ${bankColor}cc)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, transition: 'background 0.3s',
                        }}>
                            <i className="fa fa-university" style={{ fontSize: 16, color: '#fff' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>เพิ่มบัญชีธนาคาร</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>กรอกข้อมูลบัญชีสำหรับรับ-โอนเงิน</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px 24px' }}>

                    {/* Live preview */}
                    {(form.accountName || form.bankName) && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '12px 14px', borderRadius: 10,
                            background: `${bankColor}08`, border: `1.5px solid ${bankColor}30`,
                            marginBottom: 20, transition: 'all 0.2s',
                        }}>
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                                background: `${bankColor}20`, border: `1.5px solid ${bankColor}50`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 16, fontWeight: 700, color: bankColor,
                            }}>
                                {getBankInitial(form.bankName)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{form.accountName || 'ชื่อบัญชี'}</div>
                                <div style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>{form.accountNumber || '—'}</div>
                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                                    {form.bankName || 'ธนาคาร'}{form.branch ? ` · ${form.branch}` : ''}
                                </div>
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 600, borderRadius: 10, padding: '2px 8px', background: '#dcfce7', color: '#16a34a' }}>
                                ใช้งาน
                            </span>
                        </div>
                    )}

                    {/* Account info */}
                    <div style={sectionLabelStyle}>ข้อมูลบัญชี</div>
                    <Row className="mb-3">
                        <Col sm={7}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>ชื่อบัญชี <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                <Form.Control
                                    placeholder="ชื่อ-นามสกุล เจ้าของบัญชี"
                                    value={form.accountName}
                                    onChange={e => setForm(p => ({ ...p, accountName: e.target.value }))}
                                    style={inputStyle}
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={5}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>เลขที่บัญชี <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                <Form.Control
                                    placeholder="xxx-x-xxxxx-x"
                                    value={form.accountNumber}
                                    onChange={e => setForm(p => ({ ...p, accountNumber: e.target.value }))}
                                    style={{ ...inputStyle, fontFamily: 'monospace' }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Bank info */}
                    <div style={sectionLabelStyle}>ข้อมูลธนาคาร</div>
                    <Row className="mb-1">
                        <Col sm={7}>
                            <Form.Group style={{ position: 'relative' }}>
                                <Form.Label style={labelStyle}>ธนาคาร <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {form.bankName && (
                                        <div style={{
                                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                            background: `${bankColor}20`, border: `1.5px solid ${bankColor}50`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 13, fontWeight: 700, color: bankColor, transition: 'all 0.2s',
                                        }}>
                                            {getBankInitial(form.bankName)}
                                        </div>
                                    )}
                                    <Form.Control
                                        placeholder="เช่น กสิกรไทย, กรุงเทพ"
                                        value={form.bankName}
                                        onChange={e => setForm(p => ({ ...p, bankName: e.target.value }))}
                                        onFocus={() => setShowBankList(true)}
                                        onBlur={() => setTimeout(() => setShowBankList(false), 150)}
                                        style={inputStyle}
                                    />
                                </div>
                                {/* Quick-select bank chips */}
                                {showBankList && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
                                        background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.10)', padding: '6px 4px',
                                        display: 'flex', flexWrap: 'wrap', gap: 4,
                                    }}>
                                        {BANK_LIST.map(b => {
                                            const c = getBankColor(b);
                                            return (
                                                <button key={b} type="button"
                                                    onMouseDown={() => setForm(p => ({ ...p, bankName: b }))}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                                        padding: '4px 9px', borderRadius: 8, cursor: 'pointer',
                                                        background: form.bankName === b ? `${c}20` : '#f9fafb',
                                                        border: `1.5px solid ${form.bankName === b ? c + '60' : '#e5e7eb'}`,
                                                        fontSize: 11, fontWeight: 600, color: form.bankName === b ? c : '#374151',
                                                    }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, flexShrink: 0 }} />
                                                    {b}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col sm={5}>
                            <Form.Group>
                                <Form.Label style={labelStyle}>สาขา</Form.Label>
                                <Form.Control
                                    placeholder="ชื่อสาขา (ถ้ามี)"
                                    value={form.branch}
                                    onChange={e => setForm(p => ({ ...p, branch: e.target.value }))}
                                    style={inputStyle}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '14px 24px', gap: 8 }}>
                    <Button variant="light" onClick={handleClose} style={{ fontSize: 14, borderRadius: 8 }}>ยกเลิก</Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={submitting} style={{ fontSize: 14, borderRadius: 8, fontWeight: 600, minWidth: 90 }}>
                        {submitting ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />บันทึก...</> : 'บันทึก'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const addBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 8, color: '#4f46e5', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const sectionLabelStyle = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, marginTop: 2 };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle = { fontSize: 14, borderRadius: 8, border: '1.5px solid #e5e7eb' };

export default AccountsBox;
